import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { AppState } from '@nxsys/store';
import type { NotificationItem } from '@nxsys/store/slices/notification/types';
import {
  isReportExportType,
  REPORT_BUILDER_EXPORT_FAILED,
} from '@nxsys/utils/report-export-download';
import { showMessage } from '@nxsys/utils/message';
import { downloadReportExport } from '../exportDownload';

// The server reports an export that is still running after this long as
// failed, so no bell entry comes for it any more.
const STALE_AFTER_MS = 3 * 60 * 60 * 1000;

type AwaitedExport = { exportId: string; startedAt: number };
type EarlyEntry = { item: NotificationItem; arrivedAt: number };

// The exports this tab started. Each one downloads, or is reported as failed,
// when its bell entry comes in, wherever the user is in the app ("Keep
// working: it downloads when ready").
const awaited: AwaitedExport[] = [];
// This user's entries for exports the tab is not waiting for (yet): a quick
// job can finish before the start request has answered with the export's id.
const early: EarlyEntry[] = [];

const dropStale = () => {
  const now = Date.now();
  for (let i = awaited.length - 1; i >= 0; i -= 1) {
    if (now - awaited[i].startedAt > STALE_AFTER_MS) awaited.splice(i, 1);
  }
  for (let i = early.length - 1; i >= 0; i -= 1) {
    if (now - early[i].arrivedAt > STALE_AFTER_MS) early.splice(i, 1);
  }
};

const finish = async (item: NotificationItem) => {
  if (item.notificationType === REPORT_BUILDER_EXPORT_FAILED) {
    showMessage({ type: 'error', content: 'Your report export failed' });
    return;
  }
  if (await downloadReportExport(item.fromId)) {
    showMessage({
      type: 'success',
      content: 'Your files have been downloaded successfully',
    });
  }
};

export const awaitReportExport = (exportId: string) => {
  dropStale();
  const at = early.findIndex((e) => e.item.fromId === exportId);
  if (at >= 0) {
    finish(early.splice(at, 1)[0].item);
    return;
  }
  awaited.push({ exportId, startedAt: Date.now() });
};

// A bell entry names its export in `fromId`.
const settle = (item: NotificationItem) => {
  const at = awaited.findIndex((e) => e.exportId === item.fromId);
  if (at < 0) {
    early.push({ item, arrivedAt: Date.now() });
    return;
  }
  awaited.splice(at, 1);
  finish(item);
};

// Calls `onFresh` with this user's Report Builder export entries that came in
// since the hook mounted, oldest first. An admin's entry also reaches the
// tenant's other admins, so only the entries assigned to this user count.
const useFreshExportEntries = (onFresh: (mine: NotificationItem[]) => void) => {
  const list = useSelector(
    (state: AppState) => state.notificationStore?.notificationList?.data,
  ) as NotificationItem[] | undefined;
  const userId = useSelector((state: AppState) => state.appStore?.user?.id);
  const seen = useRef<Set<string> | null>(null);
  const onFreshRef = useRef(onFresh);
  onFreshRef.current = onFresh;

  useEffect(() => {
    const items = list ?? [];
    if (!seen.current) {
      seen.current = new Set(items.map((item) => item.id));
      return;
    }
    const known = seen.current;
    const fresh = items.filter((item) => !known.has(item.id));
    fresh.forEach((item) => known.add(item.id));
    const mine = fresh.filter(
      (item) =>
        isReportExportType(item.notificationType) &&
        !!userId &&
        item.assignedTo === userId,
    );
    // The list is newest first.
    if (mine.length) onFreshRef.current(mine.reverse());
  }, [list, userId]);
};

// Mounted once for the whole app (AppLayout): saves the files this tab is
// waiting for, or reports their failure, on whatever page the user is.
export const useReportExportDownloads = () =>
  useFreshExportEntries((mine) => {
    dropStale();
    mine.forEach(settle);
  });

// A Report Builder page: runs `onArrive` when an export of this user finishes
// or fails (the download itself is useReportExportDownloads').
export const useReportExportNotifications = (onArrive?: () => void) =>
  useFreshExportEntries(() => onArrive?.());
